/**
 * Onboarding: which top-level screen the app shows (sign in → accept invite → create workspace → workspace) and
 * which workspace opens. Pure; SessionViewModel feeds it, every platform's root view switches on the result.
 *
 * Offline-first: a signed-in user with a remembered workspace opens it immediately, before the workspace list has
 * loaded; "loading" is only shown on a first launch without one.
 */
import { isReservedSlug, isValidSlug } from "./slug.ts";

export type WorkspaceRef = { id: string; slug: string; name: string };

export type OnboardingInput = {
  signedIn: boolean;
  /** Invite token from the URL (/invite/<token>) or kept across sign-in; shown until accepted or dismissed. */
  inviteToken?: string;
  /** The caller's workspaces; undefined = not loaded yet (never fetched on this device). */
  workspaces?: WorkspaceRef[];
  /** Last opened workspace (persisted with the session). */
  lastWorkspace?: WorkspaceRef;
  /** Workspace slug named by the URL (/acme/c/…), if any. */
  urlSlug?: string;
  /** The user chose "Create workspace" (they may already have others). */
  creating: boolean;
};

export type OnboardingScreen =
  | { screen: "signIn" }
  | { screen: "invite"; token: string }
  | { screen: "loading" }
  | { screen: "createWorkspace" }
  | { screen: "workspace"; workspace: WorkspaceRef };

/**
 * The workspace to open: the one the URL names, else the last one opened, else the first. Before the list has
 * loaded, the remembered workspace (offline-first). Undefined when there is none.
 */
export function pickWorkspace(workspaces: WorkspaceRef[] | undefined, last: WorkspaceRef | undefined, urlSlug: string | undefined): WorkspaceRef | undefined {
  if (workspaces === undefined) return last;
  if (urlSlug !== undefined) {
    const byUrl = workspaces.find((w) => w.slug === urlSlug);
    if (byUrl !== undefined) return byUrl;
  }
  if (last !== undefined) {
    const byId = workspaces.find((w) => w.id === last.id);
    if (byId !== undefined) return byId;
  }
  return workspaces[0];
}

export function onboardingScreen(input: OnboardingInput): OnboardingScreen {
  if (input.inviteToken !== undefined) return { screen: "invite", token: input.inviteToken };
  if (!input.signedIn) return { screen: "signIn" };
  if (input.creating) return { screen: "createWorkspace" };
  const ws = pickWorkspace(input.workspaces, input.lastWorkspace, input.urlSlug);
  if (ws !== undefined) return { screen: "workspace", workspace: ws };
  if (input.workspaces === undefined) return { screen: "loading" };
  return { screen: "createWorkspace" };
}

/** "/invite/<token>" (optionally a full URL) → the token, if well-formed. */
export function inviteTokenFromPath(path: string): string | undefined {
  const start = path.indexOf("/invite/");
  if (start < 0) return undefined;
  if (start > 0) {
    // Only a full URL may have something before the path ("https://host/invite/…").
    const scheme = path.indexOf("://");
    if (scheme < 0 || path.indexOf("/", scheme + 3) !== start) return undefined;
  }
  let token = path.slice(start + "/invite/".length);
  for (const stop of ["?", "#", "/"]) {
    const i = token.indexOf(stop);
    if (i >= 0) token = token.slice(0, i);
  }
  if (token.length < 16 || token.length > 128) return undefined;
  for (let i = 0; i < token.length; i++) {
    const c = token.charCodeAt(i);
    const ok = (c >= 0x30 && c <= 0x39) || (c >= 0x41 && c <= 0x5a) || (c >= 0x61 && c <= 0x7a) || c === 0x2d || c === 0x5f;
    if (!ok) return undefined;
  }
  return token;
}

/** First path segment when it can be a workspace slug ("/acme/c/1" → "acme"; "/invite/…", "/" → undefined). */
export function workspaceSlugFromPath(path: string): string | undefined {
  const clean = path.split("?")[0]!.split("#")[0]!;
  const segment = clean.split("/").find((s) => s !== "");
  if (segment === undefined) return undefined;
  const slug = segment.toLowerCase();
  return isValidSlug(slug) && !isReservedSlug(slug) ? slug : undefined;
}
